import type { Primitive } from 'type-fest';

const DIACRITICAL_MARKS_REGEX = /[\u0300-\u036F]/g;

/**
 * Remove diacritical marks from a string.
 */
export function unaccent(value: Primitive): string {
  if (!value) {
    return '';
  }

  return value
    .toString()
    .normalize('NFD')
    .replaceAll(DIACRITICAL_MARKS_REGEX, '');
}

/**
 * Remove diacritical marks from a string and convert it to lowercase.
 */
export function unaccentLower(value: Primitive): string {
  return unaccent(value).toLowerCase();
}

/**
 * Create a searchable index value from an array of primitive values.
 */
export function createSearchableIndexValue(values: Primitive[]): string {
  return values
    .map(value => unaccentLower(value))
    .join(' ')
    .trim();
}

/**
 * Create a search expression for a given value, suitable for
 * $like search queries.
 */
export function createSearchExpression(
  value: string | undefined | null,
): string | undefined {
  if (!value) {
    return undefined;
  }

  return `%${unaccentLower(value)}%`;
}

/**
 * Normalizes a website URL for consistent storage and deduplication.
 * - Ensures https:// protocol
 * - Lowercases hostname
 * - Strips www. prefix
 * - Strips trailing slash
 * - Keeps the query string and hash fragment intact — they distinguish
 *   otherwise-identical paths (e.g. `?page=2`, `#section`) and must not be
 *   treated as the same page for storage/deduplication purposes.
 */
export function normalizeWebsiteUrl(input: string): string {
  let url = input.trim();

  if (!/^https?:\/\//i.test(url)) {
    url = `https://${url}`;
  }

  const parsed = new URL(url);

  let hostname = parsed.hostname.toLowerCase();

  if (hostname.startsWith('www.')) {
    hostname = hostname.slice(4);
  }

  let path = parsed.pathname;

  path = path === '/' ? '' : path.replace(/\/+$/, '');

  return `https://${hostname}${path}${parsed.search}${parsed.hash}`;
}

const WEBSITE_HOSTNAME_REGEX = /^[\d.a-z-]+\.[a-z]{2,}$/i;

export function parseWebsiteUrlLines(value: unknown): string[] {
  if (typeof value === 'string') {
    return value
      .split('\n')
      .map(line => line.trim())
      .filter(line => line !== '');
  }

  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => typeof entry === 'string');
  }

  return [];
}

export function isValidWebsiteUrl(input: string): boolean {
  const normalized = tryNormalizeWebsiteUrl(input);

  if (!normalized) {
    return false;
  }

  try {
    const hostname = new URL(normalized).hostname.toLowerCase();

    if (hostname === 'localhost') {
      return true;
    }

    return WEBSITE_HOSTNAME_REGEX.test(hostname);
  } catch {
    return false;
  }
}

export function tryNormalizeWebsiteUrl(input: string): string | null {
  try {
    return normalizeWebsiteUrl(input);
  } catch {
    return null;
  }
}

// Re-export shared constants
export * from './constants';
